import { Controller } from "@hotwired/stimulus"
import Trix from "trix"

// « @ » dans l'éditeur d'une note, comme sur Slack : propose les personnes qui peuvent lire
// la note (Comments::MentionsController) et insère celle qu'on choisit comme pièce jointe
// Trix — son sgid et son rendu (comments/_mention). ActionText relit ce sgid à
// l'enregistrement, c'est lui qui déclenche la notification (Comments::ActivityNotifier).
//
// Le menu ne vit pas dans le formulaire : `.rich_text_area` rogne ce qui en dépasse. Il est
// posé dans la modale (ou le body), en position fixe sous le « @ ». Le clavier est intercepté
// en phase de capture, avant Trix et avant comment-form : quand le menu est ouvert, Entrée
// choisit une personne au lieu d'envoyer la note.

const MENTION_CONTENT_TYPE = "application/vnd.depozen.mention"
const MAX_QUERY_LENGTH = 40
const MAX_QUERY_WORDS = 3
const FETCH_DELAY_MS = 100
const MENU_GAP_PX = 6
const VIEWPORT_MARGIN_PX = 8
// Ce qui peut précéder le « @ » d'une mention : rien, un blanc, une parenthèse. Pas une
// lettre : « prenom@agence.fr » reste une adresse.
const TRIGGER_PREFIX = /[\s(\u00a0]/
// Retour à la ligne ou pièce jointe (une mention déjà insérée) : on est sorti de la mention.
const QUERY_BREAK = /[\n\uFFFC]/

let menuSequence = 0

export default class extends Controller {
  static values = { url: String, label: String }

  connect() {
    this.suggestionCache = new Map()
    this.activeIndex = -1
    this.mentionTrigger = null
    this.dismissedPosition = null

    this.boundKeydown = this.onKeydown.bind(this)
    this.boundRefresh = this.refresh.bind(this)
    this.boundBlur = this.onBlur.bind(this)
    this.boundReposition = this.reposition.bind(this)
    this.boundBeforeCache = this.teardownMenu.bind(this)

    this.element.addEventListener("keydown", this.boundKeydown, true)
    this.element.addEventListener("trix-change", this.boundRefresh)
    this.element.addEventListener("trix-selection-change", this.boundRefresh)
    this.element.addEventListener("trix-blur", this.boundBlur)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.boundKeydown, true)
    this.element.removeEventListener("trix-change", this.boundRefresh)
    this.element.removeEventListener("trix-selection-change", this.boundRefresh)
    this.element.removeEventListener("trix-blur", this.boundBlur)
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    this.teardownMenu()
  }

  // --- Détection du « @ » ------------------------------------------------------------------

  refresh(event) {
    const editorElement = event?.target?.closest?.("trix-editor")
    if (!editorElement?.editor) return

    const trigger = this.findTrigger(editorElement.editor)
    if (!trigger || trigger.position === this.dismissedPosition) {
      if (!trigger) this.dismissedPosition = null
      this.close()
      return
    }

    // Trix émet trix-change puis trix-selection-change à chaque frappe : la même requête ne
    // se relance pas, et celle qui est en vol reste valable.
    if (this.sameTrigger(this.mentionTrigger, trigger)) return

    this.editorElement = editorElement
    this.mentionTrigger = trigger
    this.scheduleFetch(trigger)
  }

  sameTrigger(a, b) {
    return Boolean(a && b) && a.position === b.position && a.query === b.query
  }

  // Le texte qui précède le curseur se termine-t-il par « @requête » ?
  findTrigger(editor) {
    const [start, end] = editor.getSelectedRange()
    if (start !== end) return null

    const textBefore = editor.getDocument().toString().slice(0, start)
    const position = textBefore.lastIndexOf("@")
    if (position === -1) return null
    if (position > 0 && !TRIGGER_PREFIX.test(textBefore[position - 1])) return null

    const query = textBefore.slice(position + 1)
    if (query.length > MAX_QUERY_LENGTH || QUERY_BREAK.test(query)) return null
    if (/^\s/.test(query) || /\s\s$/.test(query)) return null
    if (query.trim().split(/\s+/).filter(Boolean).length > MAX_QUERY_WORDS) return null

    return { position, query: query.trim(), spaced: /\s/.test(query) }
  }

  // --- Suggestions --------------------------------------------------------------------------

  scheduleFetch(trigger) {
    clearTimeout(this.fetchTimer)
    this.fetchTimer = setTimeout(() => this.loadSuggestions(trigger), FETCH_DELAY_MS)
  }

  async loadSuggestions(trigger) {
    let html
    try {
      html = await this.fetchSuggestions(trigger.query)
    } catch (error) {
      if (error.name !== "AbortError") this.close()
      return
    }

    // Le curseur a pu bouger pendant la requête.
    if (!this.sameTrigger(this.mentionTrigger, trigger)) return
    this.render(html, trigger)
  }

  async fetchSuggestions(query) {
    const key = query.toLowerCase()
    if (this.suggestionCache.has(key)) return this.suggestionCache.get(key)

    this.fetchAbort?.abort()
    this.fetchAbort = new AbortController()

    const url = new URL(this.urlValue, window.location.href)
    url.searchParams.set("q", query)

    const response = await fetch(url, {
      headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin",
      signal: this.fetchAbort.signal,
    })
    // Déconnecté ou refusé : Devise redirige vers la connexion, pas question de l'afficher.
    if (!response.ok || response.redirected) throw new Error(`mentions: HTTP ${response.status}`)

    const html = await response.text()
    this.suggestionCache.set(key, html)
    return html
  }

  render(html, trigger) {
    const menu = this.ensureMenu()
    menu.innerHTML = html

    const options = this.options()
    // Plusieurs mots sans résultat : on écrit une phrase après un « @ », pas une mention.
    if (options.length === 0 && trigger.spaced) {
      this.close()
      return
    }

    options.forEach((option, index) => {
      option.id = `${menu.id}-option-${index}`
    })
    this.activeIndex = options.length > 0 ? 0 : -1
    this.syncActiveOption()

    menu.hidden = false
    this.setExpanded(true)
    this.reposition()
  }

  // --- Clavier et souris --------------------------------------------------------------------

  onKeydown(event) {
    if (!this.isOpen() || event.isComposing) return
    if (!event.target.closest?.("trix-editor")) return

    const options = this.options()
    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        if (options.length === 0) return
        this.moveActive(event.key === "ArrowDown" ? 1 : -1)
        break
      case "Enter":
      case "Tab":
        if (this.activeIndex < 0 || !options[this.activeIndex]) return
        this.insertMention(options[this.activeIndex])
        break
      case "Escape":
        this.dismissedPosition = this.mentionTrigger?.position ?? null
        this.close()
        break
      default:
        return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
  }

  // Le clic ne doit pas retirer le focus de l'éditeur, qui perdrait sa sélection.
  onMenuMousedown(event) {
    event.preventDefault()
    const option = event.target.closest('[role="option"]')
    if (option) this.insertMention(option)
  }

  onMenuMousemove(event) {
    const option = event.target.closest('[role="option"]')
    if (!option) return

    const index = this.options().indexOf(option)
    if (index === -1 || index === this.activeIndex) return
    this.activeIndex = index
    this.syncActiveOption({ scroll: false })
  }

  onBlur() {
    // Laisse passer un clic dans le menu (mousedown sans perte de focus) avant de fermer.
    setTimeout(() => {
      if (document.activeElement !== this.editorElement) this.close()
    }, 0)
  }

  moveActive(step) {
    const count = this.options().length
    this.activeIndex = (this.activeIndex + step + count) % count
    this.syncActiveOption()
  }

  syncActiveOption({ scroll = true } = {}) {
    const options = this.options()
    options.forEach((option, index) => {
      const active = index === this.activeIndex
      option.classList.toggle("comment-mentions__option--active", active)
      option.setAttribute("aria-selected", active ? "true" : "false")
      if (active && scroll) option.scrollIntoView({ block: "nearest" })
    })

    const activeOption = options[this.activeIndex]
    if (activeOption) {
      this.editorElement?.setAttribute("aria-activedescendant", activeOption.id)
    } else {
      this.editorElement?.removeAttribute("aria-activedescendant")
    }
  }

  // --- Insertion ----------------------------------------------------------------------------

  // Remplace « @requête » par la mention, suivie d'une espace pour continuer à écrire.
  insertMention(option) {
    const editor = this.editorElement?.editor
    const trigger = this.mentionTrigger
    if (!editor || !trigger || !option.dataset.mentionSgid) return

    const attachment = new Trix.Attachment({
      sgid: option.dataset.mentionSgid,
      content: option.dataset.mentionContent,
      contentType: MENTION_CONTENT_TYPE,
    })

    const [cursor] = editor.getSelectedRange()
    editor.setSelectedRange([trigger.position, cursor])
    editor.deleteInDirection("backward")
    editor.insertAttachment(attachment)
    editor.insertString(" ")

    this.close()
  }

  // --- Menu ---------------------------------------------------------------------------------

  ensureMenu() {
    if (this.menuElement?.isConnected) return this.menuElement

    menuSequence += 1
    const menu = document.createElement("div")
    menu.id = `comment-mentions-${menuSequence}`
    menu.className = "comment-mentions"
    menu.setAttribute("role", "listbox")
    if (this.hasLabelValue) menu.setAttribute("aria-label", this.labelValue)
    menu.hidden = true
    menu.addEventListener("mousedown", (event) => this.onMenuMousedown(event))
    menu.addEventListener("mousemove", (event) => this.onMenuMousemove(event))

    // Dans la modale : le piège à focus de Bootstrap tolère ce qui s'y trouve.
    ;(this.element.closest(".modal") || document.body).appendChild(menu)
    this.menuElement = menu

    window.addEventListener("resize", this.boundReposition)
    window.addEventListener("scroll", this.boundReposition, true)
    return menu
  }

  reposition() {
    const menu = this.menuElement
    const editor = this.editorElement?.editor
    if (!menu || menu.hidden || !editor || !this.mentionTrigger) return

    const anchor = editor.getClientRectAtPosition(this.mentionTrigger.position) ||
      this.editorElement.getBoundingClientRect()
    const width = menu.offsetWidth
    const height = menu.offsetHeight
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN_PX
    const left = Math.max(VIEWPORT_MARGIN_PX, Math.min(anchor.left, maxLeft))

    // Au-dessus du « @ » dans la moitié basse de l'écran (le composeur est en bas de la
    // fenêtre des notes), dessous sinon : le côté ne change pas avec le nombre de résultats.
    const above = anchor.top - height - MENU_GAP_PX
    const below = anchor.bottom + MENU_GAP_PX
    const preferAbove = anchor.top > window.innerHeight / 2
    const fitsAbove = above >= VIEWPORT_MARGIN_PX
    const fitsBelow = below + height <= window.innerHeight - VIEWPORT_MARGIN_PX
    const top = (preferAbove && fitsAbove) || (!fitsBelow && fitsAbove) ? above : below

    menu.style.left = `${Math.round(left)}px`
    menu.style.top = `${Math.round(Math.max(VIEWPORT_MARGIN_PX, top))}px`
  }

  options() {
    return this.menuElement ? Array.from(this.menuElement.querySelectorAll('[role="option"]')) : []
  }

  isOpen() {
    return Boolean(this.menuElement && !this.menuElement.hidden)
  }

  setExpanded(expanded) {
    const editorElement = this.editorElement
    if (!editorElement) return

    editorElement.setAttribute("aria-autocomplete", "list")
    editorElement.setAttribute("aria-expanded", expanded ? "true" : "false")
    if (expanded && this.menuElement) {
      editorElement.setAttribute("aria-controls", this.menuElement.id)
    } else {
      editorElement.removeAttribute("aria-activedescendant")
    }
  }

  close() {
    clearTimeout(this.fetchTimer)
    this.fetchAbort?.abort()
    this.mentionTrigger = null
    this.activeIndex = -1

    if (this.menuElement) {
      this.menuElement.hidden = true
      this.menuElement.innerHTML = ""
    }
    this.setExpanded(false)
  }

  // Le menu est hors du contrôleur : il part avec lui, et avant l'instantané de Turbo.
  teardownMenu() {
    this.close()
    window.removeEventListener("resize", this.boundReposition)
    window.removeEventListener("scroll", this.boundReposition, true)
    this.menuElement?.remove()
    this.menuElement = null
  }
}
